import styled, { css } from 'styled-components';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { RadioButton as _RadioButton } from '@/components/UI/RadioButton';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import { AngleIcon } from '@/components/UI/Icons/Icons';
import { DropdownPopup } from '@/components/UI/DropdownPopup';
import { removeScrollbar, thinScrollbar } from '@/styles/helpers';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';

export const OptionSkeletonRow = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: 44px;
  padding-left: 15px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 60px;
    padding-left: 0;

    &:not(:last-of-type) {
      border-bottom: 1px solid ${({ theme }) => theme.palette.primary.border};
    }
  }
`;

export const OptionSkeleton = styled(SkeletonLoader).attrs({
  width: 110,
  height: 18,
  borderRadius: 7,
})`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 150px;
  }
`;

export const MenuItem = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 10px 0 15px;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.1s linear;

  &:hover {
    background-color: ${({ theme }) => theme.palette.secondary.background};
  }
`;

export const MenuItemText = styled(Typography.Body)``;

export const RadioButton = styled(_RadioButton).attrs({
  $color: 'secondary',
})``;

export const Option = styled(Checkbox).attrs({
  appearance: 'radio',
  size: 'lg',
  color: 'secondary',
})``;

export const Container = styled.div`
  position: relative;
  width: 23%;
  min-width: 90px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 19%;
    min-width: 67px;
  }
`;

export const Trigger = styled.div<{
  $isActive: boolean;
  $isHoverSuppressed: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 10px 15px;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.palette.system.blue : theme.palette.secondary.border};
  border-radius: 14px;
  background-color: ${({ theme }) => theme.palette.primary.background};
  cursor: pointer;

  ${({ $isActive }) =>
    $isActive &&
    css`
      ${DropdownIcon} {
        transform: rotate(180deg);
      }
    `}

  ${({ $isHoverSuppressed, theme }) =>
    !$isHoverSuppressed &&
    css`
      &:hover {
        border-color: ${theme.palette.system.blue};
      }
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-right: 5px;
    padding-left: 10px;
    border-radius: 12px;
  }
`;

export const TriggerLabel = styled(Typography.Numbers)<{
  $isPlaceholder?: boolean;
}>`
  font-size: 22px;

  ${({ $isPlaceholder }) =>
    $isPlaceholder &&
    css`
      letter-spacing: 3px;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 18px;

    ${({ $isPlaceholder }) =>
      $isPlaceholder &&
      css`
        letter-spacing: 2.4px;
      `}
  }
`;

export const DropdownIcon = styled(AngleIcon).attrs({
  width: 18,
  height: 18,
})`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 5px 3px;
`;

export const Dropdown = styled(DropdownPopup).attrs({
  align: 'left',
})`
  max-height: 323px;
  padding: 10px 4px 10px 0;
  overflow: hidden;
`;

export const Menu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 186px;
  max-height: 301px;
  padding: 0 10px;
  overflow-y: auto;
  ${thinScrollbar}
`;

export const Sheet = styled(BottomSheet).attrs({
  isPageLocked: true,
  hasHeadBorder: false,
  isInPortal: true,
})``;

export const SheetContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 67dvh;
`;

export const OptionsList = styled.div`
  overflow-y: auto;
  ${removeScrollbar()}
`;
